import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";

import {
  HEADER_ROW_TEMPLATE,
  ROW_TEMPLATE,
  ROW_HEIGHT,
} from "../../consts/volumeOverlayTable";

import {
  selectedOverlaySelector,
  volumeOverlayTableDataSelector,
} from "../../reducers/matrixProfile/selectors";
import { fetchVolumeData } from "../../reducers/matrixProfile/thunkActions";

import { getUpdatedRows } from "../../utils/volumeOverlayTable";
import useWindowDimensions from "../../utils/useWindowDimensions";

import Header from "../../components/Header";
import { LoadingBackdrop } from "../../components/LoadingBackdrop";
import { View } from "../../components/View";
import { RowsRendererComponent } from "./RowsRenderer";

const useStyles = makeStyles({
  appContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    overflow: "hidden",
    height: "calc(100% - 70px)",
    "& .MuiDataGrid-row.Mui-even": {
      backgroundColor: "#e2e2e2",
    },
  },
  overdrop: {
    position: "relative",
    backgroundColor: "transparent",
    height: `100%`,
    width: "100%",
    zIndex: "1",
  },
});

const VolumeOverlayTable = () => {
  const styles = useStyles();
  const overdropRef = useRef(null);
  const selectedOverlay = useSelector(selectedOverlaySelector);
  const { volumeData, volumeDate } = useSelector(
    volumeOverlayTableDataSelector
  );
  const { height } = useWindowDimensions();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [tableState, setTableState] = useState({
    clientY: 0,
    mouseOverRow: null,
    scrollOffset: 0,
    rowsOffset: 0,
    overdropHeight: 0,
    maxLastRowOffset: 0,
    headerHeight: 40,
    rowsMax: 0,
  });

  const onScreenRows = rows[0]
    ? [
        rows[0],
        ...rows.slice(
          tableState.rowsOffset + 1,
          tableState.rowsOffset + 1 + tableState.rowsMax
        ),
      ]
    : [];

  useEffect(() => {
    selectedOverlay &&
      selectedOverlay.overlayDate !== volumeDate &&
      dispatch(fetchVolumeData(selectedOverlay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedOverlay, volumeDate]);

  useEffect(() => {
    setRows([
      { ...HEADER_ROW_TEMPLATE, id: 0 },
      ...volumeData.map((row, i) => ({
        ...ROW_TEMPLATE,
        ...row,
        id: i + 1,
        name: row.timeband,
      })),
    ]);
  }, [volumeData]);

  const setRecalculatedTable = useCallback(
    (params) => {
      if (!rows || !rows.length) return;
      const { innerHeight: height } = window;

      const {
        rowsOffset,
        scrollOffset,
        overdropHeight,
        maxLastRowOffset,
        rowsMax,
        clientY,
        mouseOverRow,
      } = tableState;
      const newOverdropHeight = overdropRef?.current?.clientHeight || 0;
      const newMaxLastRowOffset = ROW_HEIGHT - (newOverdropHeight % ROW_HEIGHT);
      const newHeaderHeight = height - newOverdropHeight;
      const newRowsMax = Math.floor(newOverdropHeight / ROW_HEIGHT) + 1;
      const newTableState = {
        ...tableState,
        overdropHeight: newOverdropHeight,
        maxLastRowOffset: newMaxLastRowOffset,
        headerHeight: newHeaderHeight,
        rowsMax: newRowsMax,
        ...params,
      };

      if (rows.length <= newRowsMax) {
        newTableState.scrollOffset = 0;
        newTableState.rowsOffset = 0;
      } else if (rowsOffset + newRowsMax >= rows.length) {
        if (scrollOffset < -newMaxLastRowOffset)
          newTableState.scrollOffset = -newMaxLastRowOffset;
        newTableState.rowsOffset = rows.length - newRowsMax;
      }

      if (
        rowsOffset !== newTableState.rowsOffset ||
        scrollOffset !== newTableState.scrollOffset ||
        overdropHeight !== newTableState.overdropHeight ||
        maxLastRowOffset !== newTableState.maxLastRowOffset ||
        rowsMax !== newTableState.rowsMax ||
        clientY !== newTableState.clientY ||
        mouseOverRow !== newTableState.mouseOverRow
      )
        setTableState(newTableState);
    },
    [rows, tableState]
  );

  useEffect(setRecalculatedTable, [height, setRecalculatedTable]);

  useEffect(() => {
    const { clientY, scrollOffset, mouseOverRow, headerHeight } = tableState;
    const onScreenRowIndex = Math.floor(
      (clientY - headerHeight - scrollOffset) / ROW_HEIGHT
    );
    const row = onScreenRows[onScreenRowIndex];
    if (mouseOverRow !== row) setRecalculatedTable({ mouseOverRow: row });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const onWheel = ({ deltaY, clientY: newClientY }) => {
    const { scrollOffset, rowsOffset, rowsMax, maxLastRowOffset, clientY } =
      tableState;
    if (rows.length <= rowsMax) return;
    const yPositive = deltaY > 0;
    let newScrollOffset =
      scrollOffset + Math.floor(yPositive ? -deltaY - 1 : -deltaY);
    let newRowsOffset = rowsOffset;

    if (newScrollOffset > 0)
      while (newScrollOffset >= 0) {
        newScrollOffset -= ROW_HEIGHT;
        newRowsOffset--;
      }
    else if (newRowsOffset + rowsMax < rows.length)
      while (newScrollOffset <= -ROW_HEIGHT) {
        newScrollOffset += ROW_HEIGHT;
        newRowsOffset++;
      }

    if (!yPositive && rowsOffset === 0) newScrollOffset = 0;
    if (newRowsOffset < 0) newRowsOffset = 0;
    else if (newRowsOffset + rowsMax >= rows.length) {
      if (newScrollOffset < -maxLastRowOffset)
        newScrollOffset = -maxLastRowOffset;
      newRowsOffset = rows.length - rowsMax;
    }

    const newTableState = {
      ...tableState,
      clientY: newClientY,
      scrollOffset: newScrollOffset,
      rowsOffset: newRowsOffset,
    };
    if (
      newTableState.clientY !== clientY ||
      newTableState.scrollOffset !== scrollOffset ||
      newTableState.rowsOffset !== rowsOffset
    )
      setTableState(newTableState);
  };

  const onMouseLeave = () => setRecalculatedTable({ mouseOverRow: null });

  const onMouseMove = ({ clientY }) => setRecalculatedTable({ clientY });

  const onDoubleClick = () =>
    tableState.mouseOverRow &&
    setRows(getUpdatedRows(tableState.mouseOverRow.id, rows));

  if (!!!rows) return null;

  return (
    <View className={styles.appContainer}>
      <LoadingBackdrop />
      <Header />

      <View className={styles.content}>
        <div
          ref={overdropRef}
          className={styles.overdrop}
          onWheel={onWheel}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          onDoubleClick={onDoubleClick}
        />
        {RowsRendererComponent({
          onScreenRows,
          ...tableState,
        })}
      </View>
    </View>
  );
};

export default VolumeOverlayTable;
